import { Component, ChangeDetectionStrategy, inject, viewChild, ElementRef, effect, OnDestroy, NgZone } from '@angular/core';
import { BaseComponentTypeEnum } from '@tailormap-viewer/api';
import { LayoutService } from '../layout.service';
import { MapService } from '@tailormap-viewer/map';
import { Subject, takeUntil, take } from 'rxjs';
import { BookmarkService } from '../../services/bookmark/bookmark.service';
import { ApplicationBookmarkFragments } from '../../services/application-bookmark/application-bookmark-fragments';
import { FeatureInfoComponent } from '../../components/feature-info/feature-info/feature-info.component';
import { MapComponent } from '@tailormap-viewer/map';
import { MeasureComponent } from '../../components/toolbar/measure/measure.component';
import { ClickedCoordinatesComponent } from '../../components/toolbar/clicked-coordinates/clicked-coordinates.component';
import { StreetviewComponent } from '../../components/toolbar/streetview/streetview.component';
import { SimpleSearchComponent } from '../../components/toolbar/simple-search/simple-search.component';
import { TerrainControlsComponent } from '../../components/toolbar/terrain-controls/terrain-controls.component';
import { BackgroundLayerToggleComponent } from '../../components/background-layer-toggle/background-layer-toggle.component';
import { ZoomButtonsComponent } from '../../components/toolbar/zoom-buttons/zoom-buttons.component';
import { GeolocationComponent } from '../../components/toolbar/geolocation/geolocation.component';
import { ScaleBarComponent } from '../../components/toolbar/scale-bar/scale-bar.component';
import { MouseCoordinatesComponent } from '../../components/toolbar/mouse-coordinates/mouse-coordinates.component';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'tm-embedded-layout',
    templateUrl: './embedded-layout.component.html',
    styleUrls: [ '../base-layout/base-layout.component.css', './embedded-layout.component.css' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FeatureInfoComponent,
        MapComponent,
        MeasureComponent,
        ClickedCoordinatesComponent,
        StreetviewComponent,
        SimpleSearchComponent,
        TerrainControlsComponent,
        BackgroundLayerToggleComponent,
        ZoomButtonsComponent,
        GeolocationComponent,
        ScaleBarComponent,
        MouseCoordinatesComponent,
        AsyncPipe,
    ],
})
export class EmbeddedLayoutComponent implements OnDestroy {
  public layoutService = inject(LayoutService);
  private mapService = inject(MapService);
  private bookmarkService = inject(BookmarkService);
  private ngZone = inject(NgZone);
  public componentTypes = BaseComponentTypeEnum;

  private mapContainer = viewChild('mapContainer', { read: ElementRef });
  private mapMoved = new Subject<void>();

  private intersectionObserver = new IntersectionObserver(entries => {
    if (entries.length > 0 && entries[0].boundingClientRect.width > 0 && entries[0].boundingClientRect.height > 0) {
      this.zoomToInitialExtent();
    }
  }, { threshold: 1 });

  private resizeObserver = new ResizeObserver(entries => {
    if (entries.length > 0 && entries[0].contentRect.width > 0 && entries[0].contentRect.height > 0) {
      // ResizeObserver callbacks are not patched by zone.js, so re-enter the Angular zone explicitly
      // to avoid NgRx's strictActionWithinNgZone check rejecting dispatches triggered from here
      this.ngZone.run(() => this.zoomToInitialExtent());
    }
  });

  constructor() {
    /**
     * In case the embedded viewer is initially hidden (by the parent page) the map does not always render to initial extent
     * We need to account for the various ways hiding elements can be done (off-screen, height = 0, display none, etc).
     * We use both an IntersectionObserver and ResizeObserver to watch for changes. We do this as long as the user did not move the map.
     * The assumption here is that as long the user did not touch the map there is no harm in setting initial extent
     * We only zoom to initial extent in case there is no location inside the bookmark initially
     */
    effect(() => {
      const el = this.mapContainer()?.nativeElement;
      if (!el || this.mapMoved.closed) {
        return;
      }
      this.intersectionObserver.disconnect();
      this.intersectionObserver.observe(el);
      this.resizeObserver.disconnect();
      this.resizeObserver.observe(el);
    });
    this.mapService.hasUserInteractedWithMap$()
      .pipe(takeUntil(this.mapMoved))
      .subscribe(hasInteracted => {
        if (!hasInteracted) {
          return;
        }
        this.intersectionObserver.disconnect();
        this.resizeObserver.disconnect();
        this.mapMoved.next();
        this.mapMoved.complete();
      });
  }

  public ngOnDestroy() {
    this.intersectionObserver.disconnect();
    this.resizeObserver.disconnect();
    this.mapMoved.next();
    this.mapMoved.complete();
  }

  private zoomToInitialExtent(): void {
    this.bookmarkService.registerFragment$<string>(ApplicationBookmarkFragments.LOCATION_BOOKMARK_DESCRIPTOR)
      .pipe(take(1))
      .subscribe(fragment => {
        if (!fragment) {
          window.setTimeout(() => { this.mapService.zoomToInitialExtent(); }, 0);
        }
      });
  }

}
